import planAccessMatrix from "../generated/planAccessMatrix.json";

const PLAN_ENTRIES = Object.entries(planAccessMatrix.plans || {});
const DEFAULT_PLAN_ID = "neo";

export const PLAN_VERSION = planAccessMatrix.version || 1;

export const PLAN_IDS = PLAN_ENTRIES.reduce((acc, [planId]) => {
    acc[planId.toUpperCase()] = planId;
    return acc;
}, {});

const featureKeys = new Set();
PLAN_ENTRIES.forEach(([, planConfig]) => {
    Object.keys(planConfig.features || {}).forEach((featureKey) => featureKeys.add(featureKey));
});

export const FEATURE_KEYS = Array.from(featureKeys).sort().reduce((acc, featureKey) => {
    const constantName = featureKey.toUpperCase();
    acc[constantName] = featureKey;
    return acc;
}, {});

export const PLAN_LABELS = PLAN_ENTRIES.reduce((acc, [planId, planConfig]) => {
    acc[planId] = planConfig.label || planId;
    return acc;
}, {});

export const PLAN_FEATURES = PLAN_ENTRIES.reduce((acc, [planId, planConfig]) => {
    acc[planId] = { ...(planConfig.features || {}) };
    return acc;
}, {});

export const normalizePlanId = (planId) => {
    if (typeof planId !== "string") {
        return DEFAULT_PLAN_ID;
    }

    const normalized = planId.trim().toLowerCase();
    return PLAN_FEATURES[normalized] ? normalized : DEFAULT_PLAN_ID;
};

export const listAllFeatureKeys = () => Array.from(featureKeys);

export const listEnabledFeatureKeys = (featureMap = {}) => {
    return Object.entries(featureMap)
        .filter(([, enabled]) => enabled === true)
        .map(([featureKey]) => featureKey)
        .sort();
};

export const expandFeatureList = (featureList = []) => {
    if (Array.isArray(featureList)) {
        return featureList.reduce((acc, featureKey) => {
            if (typeof featureKey === "string" && featureKey) {
                acc[featureKey] = true;
            }
            return acc;
        }, {});
    }

    if (featureList && typeof featureList === "object") {
        return { ...featureList };
    }

    return {};
};

export const resolvePlanFeatures = (planId, featureOverrides = {}, effectiveFeatures = null) => {
    const normalizedPlanId = normalizePlanId(planId);
    const defaultFeatures = PLAN_FEATURES[normalizedPlanId] || PLAN_FEATURES[DEFAULT_PLAN_ID];
    const resolvedEffectiveFeatures = expandFeatureList(effectiveFeatures);

    if (Object.keys(resolvedEffectiveFeatures).length > 0) {
        return { ...defaultFeatures, ...resolvedEffectiveFeatures };
    }

    return { ...defaultFeatures, ...(featureOverrides || {}) };
};

export const getPlanDefinition = (planId) => {
    const normalizedPlanId = normalizePlanId(planId);

    return {
        id: normalizedPlanId,
        label: PLAN_LABELS[normalizedPlanId] || normalizedPlanId,
        features: resolvePlanFeatures(normalizedPlanId),
    };
};

export const compactFeatureFlags = (featureMap = {}) => listEnabledFeatureKeys(featureMap);

export const PLAN_ACCESS_MATRIX = planAccessMatrix;
